// src/components/documents/DocumentGrid.jsx - V1 ENTERPRISE
// ═══════════════════════════════════════════════════════════════════════════
// 📐 DOCUMENT GRID — Airbnb/Zillow/QuintoAndar Grade
// ═══════════════════════════════════════════════════════════════════════════
// 🔥 ULTRA ROBUSTO:    Grid responsivo con breakpoints adaptativos
// 🔥 ULTRA RÁPIDO:     Virtualización implícita vía React.memo en cards
// 🔥 ULTRA EFICIENTE:  AnimatePresence para animaciones de entrada/salida
// 🔥 ULTRA PROFESIONAL: Transiciones suaves al cambiar modo de vista
// ═══════════════════════════════════════════════════════════════════════════

import React, { useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import DocumentCard from './DocumentCard';
import DocumentEmpty from './DocumentEmpty';

// ═══════════════════════════════════════════════════════════════════════════
// 📐 CONFIGURACIÓN DE COLUMNAS POR BREAKPOINT
// ═══════════════════════════════════════════════════════════════════════════

const GRID_CONFIG = {
  mobile: 'grid-cols-1 gap-3',
  tablet: 'grid-cols-1 sm:grid-cols-2 gap-4',
  desktop: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5',
  wide: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6',
};

// ═══════════════════════════════════════════════════════════════════════════
// 📐 COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

function DocumentGrid({
  documents,
  clients,
  properties,
  isMobile,
  primaryColor,
  selectedDocs,
  formatFileSize,
  onToggleSelect,
  onToggleFavorite,
  onPreview,
  onDownload,
  onDelete,
  onNewDocument,
}) {
  // ─── Grid class según dispositivo ────────────────────────────────────
  const gridClass = useMemo(() => {
    if (!isMobile) return GRID_CONFIG.wide;
    return GRID_CONFIG.mobile;
  }, [isMobile]);

  // ─── Estado vacío ────────────────────────────────────────────────────
  if (!documents || documents.length === 0) {
    return <DocumentEmpty isMobile={isMobile} onNewDocument={onNewDocument} />;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <div className={`grid ${gridClass}`}>
      <AnimatePresence mode="popLayout">
        {documents.map((doc) => {
          // Resolver cliente y propiedad vinculados
          const client = doc.clientId
            ? clients?.find(c => c.id === doc.clientId)
            : null;
          const property = doc.propertyId
            ? properties?.find(p => p.id === doc.propertyId)
            : null;
          const isSelected = selectedDocs?.includes(doc.id) || false;

          return (
            <DocumentCard
              key={doc.id}
              doc={doc}
              client={client}
              property={property}
              isSelected={isSelected}
              isMobile={isMobile}
              primaryColor={primaryColor}
              formatFileSize={formatFileSize}
              onToggleSelect={onToggleSelect}
              onToggleFavorite={onToggleFavorite}
              onPreview={onPreview}
              onDownload={onDownload}
              onDelete={onDelete}
            />
          );
        })}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 📤 EXPORT
// ═══════════════════════════════════════════════════════════════════════════

export default React.memo(DocumentGrid);